import type { MediaCaption, Stuff, StuffMedia as Picture } from "@69ly/shared";
import { useRef, useState, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { ClipPlayer } from "./ClipPlayer.tsx";
import { Icon } from "./Icon.tsx";
import { useImageViewer, type ViewerImage } from "./ImageViewer.tsx";

/** YouTube's privacy-friendly player, starting and ending at the clip; on its own and silent with `auto`. */
export function youtubeEmbed(y: NonNullable<Stuff["youtube"]>, auto = false): string {
  const p = new URLSearchParams({ rel: "0", modestbranding: "1", ...(auto ? { autoplay: "1", mute: "1", playsinline: "1" } : {}) });
  if (y.start != null) p.set("start", String(y.start));
  if (y.end != null) p.set("end", String(y.end));
  return `https://www.youtube-nocookie.com/embed/${y.id}?${p.toString()}`;
}

const ORDER = ["landing", "aim", "stand", "other"] as const satisfies readonly MediaCaption[];

/** A stuff's screenshots, what it does first: landing, aim, stand, the rest (the video after them). */
export function stuffPictures(stuff: Stuff) {
  return [...stuff.media].sort((a, b) => ORDER.indexOf(a.caption) - ORDER.indexOf(b.caption));
}

/** The picture shown on a video until it plays: where to aim (closest to its first frame), else where it lands. */
export function clipPoster(stuff: Stuff): string | undefined {
  const order: MediaCaption[] = ["aim", "landing", "stand", "other"];
  return [...stuff.media].sort((a, b) => order.indexOf(a.caption) - order.indexOf(b.caption))[0]?.url;
}

/** One button of the media switch: a kind of screenshot, the stuff's own video, or YouTube. */
export type MediaKind = MediaCaption | "clip" | "youtube";

/** What a stuff has to show, in order: each kind of screenshot it has, then its video, then YouTube. */
export function mediaKinds(stuff: Stuff): MediaKind[] {
  const kinds: MediaKind[] = ORDER.filter((c) => stuff.media.some((m) => m.caption === c));
  if (stuff.clip) kinds.push("clip");
  if (stuff.youtube) kinds.push("youtube");
  return kinds;
}

/** One button per kind the stuff has: Stand, Aim, Landing, Other, Video, YouTube. */
export function MediaToggle({ stuff, kinds, value, onChange }: { stuff: Stuff; kinds: MediaKind[]; value: MediaKind; onChange: (k: MediaKind) => void }) {
  const { t } = useTranslation();
  if (kinds.length < 2) return null;
  return (
    <div className="segmented media-toggle" role="group" aria-label={t("media.show")}>
      {kinds.map((k) => {
        const n = k === "other" ? stuff.media.filter((m) => m.caption === "other").length : 0;
        return (
          <button key={k} type="button" className="seg" aria-pressed={value === k} onClick={() => onChange(k)}>
            <Icon name={k === "clip" || k === "youtube" ? "play" : "image"} size={14} />
            {k === "clip" || k === "youtube" || k === "other" ? t(`media.${k}`) : t(`detail.${k}`)}
            {n > 1 && <span className="seg-count">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** The stuff's own video, or its YouTube clip. */
export function StuffVideo({ stuff, kind }: { stuff: Stuff; kind: "clip" | "youtube" }) {
  if (kind === "clip" && stuff.clip) return <ClipPlayer src={stuff.clip.url} title={stuff.title} poster={clipPoster(stuff)} />;
  if (kind === "youtube" && stuff.youtube)
    return (
      <div className="video">
        <iframe
          src={youtubeEmbed(stuff.youtube)}
          title={stuff.title}
          allow="encrypted-media; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      </div>
    );
  return null;
}

/** The pictures as the viewer shows them, with their captions spelled out. */
export function viewerImages(stuff: Stuff, t: (k: string) => string): ViewerImage[] {
  return stuffPictures(stuff).map((m) => ({ url: m.url, caption: `${stuff.title} · ${t(`detail.${m.caption}`)}` }));
}

const ZOOMS = [1, 1.5, 2, 3, 4];

/**
 * A screenshot in place: zoom in and out with the buttons (drag to look around
 * when zoomed, double click to switch), and a button for the full-size viewer.
 */
export function ZoomShot({ picture, alt, onFullscreen, compact }: { picture: Pick<Picture, "id" | "url">; alt: string; onFullscreen: () => void; compact?: boolean }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [seen, setSeen] = useState(picture.id);
  const box = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  // Another picture: back to the whole picture.
  if (seen !== picture.id) {
    setSeen(picture.id);
    setStep(0);
    setPan({ x: 0, y: 0 });
  }
  const scale = ZOOMS[step];

  /** Keeps the zoomed picture over its frame. */
  const clamp = (p: { x: number; y: number }, s = scale) => {
    const el = box.current;
    if (!el || s === 1) return { x: 0, y: 0 };
    const mx = (el.clientWidth * (s - 1)) / 2;
    const my = (el.clientHeight * (s - 1)) / 2;
    return { x: Math.max(-mx, Math.min(mx, p.x)), y: Math.max(-my, Math.min(my, p.y)) };
  };
  const zoom = (next: number) => {
    const s = ZOOMS[next];
    setStep(next);
    setPan((p) => clamp({ x: (p.x * s) / scale, y: (p.y * s) / scale }, s));
  };
  const down = (e: PointerEvent<HTMLDivElement>) => {
    if (scale === 1) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y };
  };
  const move = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (d) setPan(clamp({ x: d.px + e.clientX - d.x, y: d.py + e.clientY - d.y }));
  };

  return (
    <figure className={`zoom-shot${compact ? " is-compact" : ""}`}>
      <div
        ref={box}
        className={`zoom-shot-frame${scale > 1 ? " is-zoomed" : ""}`}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={() => (drag.current = null)}
        onPointerCancel={() => (drag.current = null)}
        onDoubleClick={() => zoom(step === 0 ? 2 : 0)}
      >
        <img src={picture.url} alt={alt} draggable={false} style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})` }} />
      </div>
      <div className="shot-controls">
        <button type="button" className="shot-btn" aria-label={t("media.zoomOut")} title={t("viewer.zoomOut")} disabled={step === 0} onClick={() => zoom(step - 1)}>
          <Icon name="zoomOut" size={18} />
        </button>
        {scale > 1 && <span className="shot-zoom">{Math.round(scale * 100)}%</span>}
        <button type="button" className="shot-btn" aria-label={t("media.zoomIn")} title={t("viewer.zoomIn")} disabled={step === ZOOMS.length - 1} onClick={() => zoom(step + 1)}>
          <Icon name="zoomIn" size={18} />
        </button>
        <button type="button" className="shot-btn" aria-label={t("media.fullscreen", { caption: alt })} title={t("media.fullscreenShort")} onClick={onFullscreen}>
          <Icon name="fullscreen" size={18} />
        </button>
      </div>
      {!compact && <figcaption className="zoom-shot-caption">{alt}</figcaption>}
    </figure>
  );
}

/**
 * A stuff's media with one button per kind it has (each kind of screenshot, its
 * video, YouTube). Screenshots zoom in place; the full-size viewer is a button away.
 */
export function MediaViewer({ stuff, empty, preferVideo }: { stuff: Stuff; empty?: string; preferVideo?: boolean }) {
  const { t } = useTranslation();
  const kinds = mediaKinds(stuff);
  // Opens on the video when asked (a combo's throws), else on the first picture.
  const [pick, setPick] = useState<MediaKind | null>(() => (preferVideo ? (kinds.find((k) => k === "clip" || k === "youtube") ?? null) : null));
  const [other, setOther] = useState(0);
  const pictures = stuffPictures(stuff);
  const viewer = useImageViewer(viewerImages(stuff, t));
  const kind = pick && kinds.includes(pick) ? pick : kinds[0];
  if (!kind) return empty ? <p className="muted small media-empty">{empty}</p> : null;

  const ofKind = kind === "clip" || kind === "youtube" ? [] : pictures.filter((m) => m.caption === kind);
  const shown = ofKind[Math.min(kind === "other" ? other : 0, ofKind.length - 1)];
  return (
    <section className="media-viewer">
      <MediaToggle stuff={stuff} kinds={kinds} value={kind} onChange={setPick} />
      {shown ? (
        <ZoomShot picture={shown} alt={t(`detail.${shown.caption}`)} onFullscreen={() => viewer.open(pictures.indexOf(shown))} />
      ) : kind === "youtube" ? (
        <StuffVideo stuff={stuff} kind={kind} />
      ) : null}
      {/* The video is there from the start, behind the pictures: it loads while they are looked at. */}
      {stuff.clip && (
        <div className="media-clip" hidden={kind !== "clip"}>
          <ClipPlayer src={stuff.clip.url} title={stuff.title} poster={clipPoster(stuff)} active={kind === "clip"} />
        </div>
      )}
      {ofKind.length > 1 && (
        <div className="panel-thumbs" role="group" aria-label={t("detail.other")}>
          {ofKind.map((m, i) => (
            <button key={m.id} type="button" className="panel-thumb" aria-pressed={m === shown} onClick={() => setOther(i)} aria-label={`${t("detail.other")} ${i + 1}`}>
              <img src={m.url} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {viewer.viewer}
    </section>
  );
}

/** The order a throw is done in: where to stand, where to aim, where it lands; then the rest. */
const STEPS = ["stand", "aim", "landing", "other"] as const satisfies readonly MediaCaption[];

/**
 * A stuff's media for a glance (Live): one big stage with its video playing by
 * itself (silent, looping), else the aim picture, and under it a row of large
 * thumbnails, the video then 1 Stand, 2 Aim, 3 Landing, to put on the stage.
 * Keyed by the stuff, so another stuff starts on its video again.
 */
export function StageMedia({ stuff, autoPlay = true, empty }: { stuff: Stuff; autoPlay?: boolean; empty?: string }) {
  const { t } = useTranslation();
  const pictures = [...stuff.media].sort((a, b) => STEPS.indexOf(a.caption) - STEPS.indexOf(b.caption));
  const video = stuff.clip ? "clip" : stuff.youtube ? "youtube" : null;
  const [pick, setPick] = useState<string | null>(null);
  const viewer = useImageViewer(pictures.map((m) => ({ url: m.url, caption: `${stuff.title} · ${t(`detail.${m.caption}`)}` })));
  const fallback = pictures.find((m) => m.caption === "aim") ?? pictures[0] ?? null;
  const shown = pick && (pick === "video" ? video : pictures.some((m) => m.id === pick)) ? pick : video ? "video" : (fallback?.id ?? null);
  if (!shown) return empty ? <p className="muted small media-empty">{empty}</p> : null;
  const picture = pictures.find((m) => m.id === shown) ?? null;
  const poster = clipPoster(stuff);
  // The steps are numbered in the order they are done; other pictures are not.
  const steps = pictures.filter((m) => m.caption !== "other");
  return (
    <section className="stage-media" aria-label={t("stage.label", { title: stuff.title })}>
      <div className="stage-main">
        {picture && <ZoomShot picture={picture} alt={t(`detail.${picture.caption}`)} onFullscreen={() => viewer.open(pictures.indexOf(picture))} />}
        {video === "youtube" && shown === "video" && stuff.youtube && (
          <div className="video">
            <iframe
              src={youtubeEmbed(stuff.youtube, autoPlay)}
              title={stuff.title}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        )}
        {stuff.clip && (
          <div className="media-clip" hidden={shown !== "video"}>
            <ClipPlayer src={stuff.clip.url} title={stuff.title} poster={poster} active={shown === "video"} autoPlay={autoPlay} />
          </div>
        )}
      </div>
      {(video ? 1 : 0) + pictures.length > 1 && (
        <div className="stage-thumbs" role="group" aria-label={t("media.show")}>
          {video && (
            <button type="button" className="stage-thumb is-video" aria-pressed={shown === "video"} aria-label={video === "clip" ? t("media.clip") : t("media.youtube")} onClick={() => setPick("video")}>
              {poster ? <img src={poster} alt="" loading="lazy" /> : <span className="stage-thumb-blank" />}
              <span className="stage-thumb-play" aria-hidden="true">
                <Icon name="play" size={18} />
              </span>
              <span className="stage-thumb-label">{video === "clip" ? t("media.clip") : t("media.youtube")}</span>
            </button>
          )}
          {pictures.map((m) => {
            const n = steps.indexOf(m) + 1;
            return (
              <button key={m.id} type="button" className="stage-thumb" aria-pressed={shown === m.id} aria-label={t(`detail.${m.caption}`)} onClick={() => setPick(m.id)}>
                <img src={m.url} alt="" loading="lazy" />
                <span className="stage-thumb-label">
                  {n > 0 && <span className="stage-step">{n}</span>}
                  {t(`detail.${m.caption}`)}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {viewer.viewer}
    </section>
  );
}

/** Small captioned screenshots; a click opens the viewer. */
export function Thumbs({ stuff, max = 3 }: { stuff: Stuff; max?: number }) {
  const { t } = useTranslation();
  const pictures = stuffPictures(stuff);
  const viewer = useImageViewer(viewerImages(stuff, t));
  if (pictures.length === 0) return null;
  return (
    <div className="thumbs">
      {pictures.slice(0, max).map((m, i) => (
        <figure key={m.id}>
          <button type="button" className="thumb-button" onClick={() => viewer.open(i)} aria-label={t("media.enlarge", { caption: t(`detail.${m.caption}`) })}>
            <img src={m.url} alt={t(`detail.${m.caption}`)} loading="lazy" />
          </button>
          <figcaption>{t(`detail.${m.caption}`)}</figcaption>
        </figure>
      ))}
      {viewer.viewer}
    </div>
  );
}
